import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import React from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import Utilities from '@util/Utilities';
import GraphUtilities from '@app/site/secured/rateshopper/rates/dashboard/GraphUtilities';
import ReactDOMServer from 'react-dom/server';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import {
	useMarketAreaStateForMonth,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketAreaStateForMonth';
import MarketRegionStateComparisonOutput from '@app/model/market_area/MarketRegionStateComparisonOutput';

const MarketStateSnapshotMedianGraph = React.memo(() => {

	const tools = useMarketStateTools();
	const { filter } = useMarketStateFilterInputTools();
	const marketAreaStatesForMonth: MarketRegionStateComparisonOutput[] = useMarketAreaStateForMonth();

	Highcharts.SVGRenderer.prototype.symbols.cross = (x: number, y: number, w: number, h: number) => {
		return ['M', x, y, 'L', x + w, y + h, 'M', x + w, y, 'L', x, y + h, 'z'];
	};


	const normalizeStatDate = (statDate: string | undefined) => statDate?.substring(0, 10);

	const demandSeriesData = tools.marketStateSnapshot?.dailySnapshots.map((ms, index) => {
		const msStatDate = normalizeStatDate(ms.statDate);
		let marketAreaState = marketAreaStatesForMonth.find(s => normalizeStatDate(s?.current?.statDate) === msStatDate);
		if (!marketAreaState) {
			marketAreaState = marketAreaStatesForMonth[index];
		}
		const demand = marketAreaState?.current?.demand;
		if (demand === null || demand === undefined) {
			return {
				name: Utilities.formatDateString(ms.statDate, 'DD MMMM YYYY', false),
				y: null,
				displayValue: 'N/A',
			};
		}
		const demandPercent = Math.max(0, Math.min(100, demand <= 1 ? demand * 100 : demand));
		return {
			name: Utilities.formatDateString(ms.statDate, 'DD MMMM YYYY', false),
			y: demandPercent,
			val: demandPercent,
			displayValue: `${Utilities.formatDouble(demandPercent, 0)}%`,
		};
	}) || [];

	const options = {
		chart: {
			type: 'line',
			height: '700',
		},
		title: {
			text: undefined,
		},
		xAxis: {
			categories: tools.marketStateSnapshot?.dailySnapshots.map(ms => Utilities.formatDateString(ms.statDate, 'MMM D', false)),
			alternateGridColor: '#f3f4f6',
			labels: {
				step: 4,
			},
			crosshair: true,
		},
		yAxis: [
			{
				title: {
					text: 'Price',
				},
			},
			{
				title: {
					text: 'Market Demand',
				},
				min: 0,
				max: 100,
				opposite: true,
				labels: {
					format: '{value}%',
				},
				gridLineWidth: 0,
			},
		],
		tooltip: {
			shared: true,
			useHTML: true,
			// @ts-ignore
			formatter: function() {
				// @ts-ignore
				const chart = this.points[0].series.chart;
				// @ts-ignore
				const xValue = this.points[0].point.x;

				// Get all series data for this x-value, including hidden ones
				// @ts-ignore
				const allPoints = [];
				// @ts-ignore
				chart.series.forEach(series => {
					const point = series.data[xValue];
					const hasYValue = point && point.y !== null && point.y !== undefined;
					const hasRangeValue = point && point.low !== null && point.low !== undefined && point.high !== null && point.high !== undefined;
					if (hasYValue || hasRangeValue) {
						allPoints.push({
							series: series,
							point: point,
							color: series.color,
						});
					}
				});

				// Sort points: general stats first, then hotel prices
				// @ts-ignore
				const sortedPoints = allPoints.sort((a, b) => {
					// @ts-ignore
					const getSortOrder = (seriesName) => {
						// General statistics first
						if (seriesName === 'Total Availability') return 1;
						if (seriesName === 'Sold Out Hotels') return 2;
						if (seriesName === 'Market Demand') return 3;
						if (seriesName === 'Competitors Price Range') return 3;
						if (seriesName === 'Competitors ' + tools.companyHotel.comparisonLabel()) return 4;

						// Main hotel next
						if (a.series.options.name === tools.companyHotel.mainHotel.competitors[0].hotel.name) return 5;

						// Individual competitors last
						return 6;
					};

					return getSortOrder(a.series.name) - getSortOrder(b.series.name);
				});

				// @ts-ignore
				return ReactDOMServer.renderToString(formatTooltip(sortedPoints, this.points[0]));
			},
			valueDecimals: 2,
		},
		series: [
			{
				type: 'column',
				name: 'Market Demand',
				data: demandSeriesData,
				yAxis: 1,
				color: 'rgba(168, 85, 247, 0.45)',
				zIndex: 0,
				borderWidth: 0,
				pointPadding: 0.05,
				groupPadding: 0.1,
			},
			GraphUtilities.competitorsPriceRangeSeries(tools.marketStateSnapshot?.dailySnapshots!),
			GraphUtilities.competitorsMedianLineSeries(tools.marketStateSnapshot?.dailySnapshots!, tools.companyHotel),
			GraphUtilities.soldOutHotelsSeries(tools.marketStateSnapshot?.dailySnapshots!),
			GraphUtilities.availabilitySeries(tools.marketStateSnapshot?.dailySnapshots!),
			...GraphUtilities.sortAllSeriesByAveragePrice(
				tools.companyHotel,
				tools.marketStateSnapshot?.dailySnapshots!,
				true,
				true,
				true,
				filter.competitorTagIds,
			),
		],

	};

	const formatTooltip = (points: any[], sharedPoint: any) => {
		return <table className="min-w-full divide-y divide-gray-300 text-gray-500">
			<thead>
			<tr>
				<th scope="col"
						className="py-2 px-3 text-left text-xs font-semibold text-yellow-900 sm:pl-0" colSpan={3}>
					{sharedPoint.point.category}
				</th>
			</tr>
			</thead>
			<tbody className="divide-y divide-gray-200 bg-white">
			{points.map((point, idx) => {
				// Handle different data types for line charts vs bar charts
				let displayValue;
				if (point.series.name === 'Competitors Price Range') {
					// Handle arearange series - show min-max range
					const low = point.point.low;
					const high = point.point.high;
					displayValue = low !== null && low !== undefined && high !== null && high !== undefined
						? `${Utilities.formatDouble(low, 0)} - ${Utilities.formatDouble(high, 0)}`
						: 'N/A';
				} else {
					displayValue = point.point.displayValue ||
						(point.series.name === 'Sold Out Hotels' ? `${point.point.y}/${point.point.total}` :
							point.series.name === 'Total Availability' ? point.point.y.toString() :
								point.point.val ? Utilities.formatDouble(point.point.val, 0) : 'N/A');
				}

				return <tr key={idx}>
					<td className="whitespace-nowrap px-2 py-2 text-xs"
							style={{ color: point.series.color }}>{point.series.name}</td>
					<td className="whitespace-nowrap px-2 py-2 text-xs" style={{ textAlign: 'right' }}>
						<b>{displayValue}</b>
					</td>
				</tr>;
			})}
			</tbody>
		</table>;
	};

	return <HighchartsReact
		highcharts={Highcharts}
		highchartsMore={HighchartsMore}
		options={options}
	/>;
});

export default MarketStateSnapshotMedianGraph;